import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable, Subscription, take } from 'rxjs';

import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { bookingHasDisruption } from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { FinnairCancelIneligibilityReason, FinnairChangeEligibility, FinnairOrder } from '@fcom/dapi/api/models';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { unsubscribe } from '@fcom/core/utils';

import { Notifications } from '../../../../interfaces';
import { NotificationsService } from '../../../../services/notifications.service';
import { BoundSelectionType } from '../../../../enums';

const CANCEL_INELIGIBILITY_REASONS_WHEN_NOT_TO_SHOW_BUTTON: FinnairCancelIneligibilityReason[] = [
  FinnairCancelIneligibilityReason.ALL_FLIGHTS_ALREADY_FLOWN,
  FinnairCancelIneligibilityReason.HAS_DISRUPTION,
  FinnairCancelIneligibilityReason.TRAVEL_AGENT_TICKETS,
  FinnairCancelIneligibilityReason.ID_TICKET,
];

@Component({
  selector: 'fin-mmb-modify-trip',
  templateUrl: './modify-trip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingModifyTripComponent implements OnInit, OnDestroy {
  readonly IconPosition = IconPosition;
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;

  @Input() booking$: Observable<FinnairOrder>;
  @Input() changeFlightModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() showTravelExtrasButton: boolean;
  @Output() emailModalOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() scrollToTravelExtras: EventEmitter<void> = new EventEmitter<void>();

  boundNotifications$: Observable<Record<string, string>>;
  modificationNotAllowedNotifications$: Observable<Notifications>;
  changeableBoundIds$: Observable<string[]>;
  cancelModalOpen$ = new BehaviorSubject<boolean>(false);
  showCancelButton$: Observable<boolean>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private gtmService: GtmService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit(): void {
    const changeEligibilities$: Observable<FinnairChangeEligibility[]> = this.booking$.pipe(
      map((booking) => booking?.eligibilities?.change ?? [])
    );
    this.changeableBoundIds$ = changeEligibilities$.pipe(
      map(
        (eligibilities) =>
          eligibilities?.filter((eligibility) => eligibility.isAllowedToUse).map((eligibility) => eligibility.id) ?? []
      )
    );

    this.showCancelButton$ = this.booking$.pipe(
      map(
        (booking) =>
          booking?.eligibilities?.voluntaryCancel?.every(
            (eligibility) => !CANCEL_INELIGIBILITY_REASONS_WHEN_NOT_TO_SHOW_BUTTON.includes(eligibility.reason)
          ) && !bookingHasDisruption(booking)
      )
    );

    this.modificationNotAllowedNotifications$ =
      this.notificationsService.getModificationNotAllowedNotificationsForTrip();
    this.boundNotifications$ = this.notificationsService.getChangeNotAllowedReasonsByBound();

    this.subscriptions.add(
      combineLatest([changeEligibilities$, this.booking$])
        .pipe(take(1))
        .subscribe(([changeEligibilities, booking]) => {
          const boundsAllowed: boolean[] = changeEligibilities.map((eligibility) => eligibility.isAllowedToUse);
          const boundsAllowedString = (boundsAllowedList: boolean[]): string => {
            if (boundsAllowedList.some(Boolean)) {
              return boundsAllowedList.every(Boolean) ? BoundSelectionType.ALL : BoundSelectionType.SOME;
            }
            return BoundSelectionType.NONE;
          };
          const reasons: string = changeEligibilities
            .map((eligibility) => eligibility.reason)
            .filter(Boolean)
            .join('::');
          const reasonsString = reasons ? `reasons-${reasons}` : '';
          this.gtmService.trackElement(
            `view-pnr_change_allow-${boundsAllowedString(boundsAllowed)}`,
            GaContext.MANAGE_BOOKING,
            ElementTypes.BUTTON,
            `${reasonsString}`,
            ElementActions.VIEW,
            `recLoc-${booking.otherInformation.analyticsToken}`
          );
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  changeFlight(order: FinnairOrder, changeableBoundIds: string[]): void {
    this.callbackOrOpenEmailModalIfPassengerDoesNotHaveEmail(() => {
      this.gtmService.trackElement(
        'view-pnr_change-init',
        GaContext.MANAGE_BOOKING,
        ElementTypes.BUTTON,
        `${changeableBoundIds.join('::')}`,
        ElementActions.CLICK,
        `recLoc-${order.otherInformation.analyticsToken}`
      );
      this.changeFlightModalOpen$.next(true);
    }, order);
  }

  openCancelBookingModal(order: FinnairOrder): void {
    this.callbackOrOpenEmailModalIfPassengerDoesNotHaveEmail(() => this.cancelModalOpen$.next(true), order);
  }

  scrollToTravelExtrasContent(): void {
    this.gtmService.trackElement(
      'add-extras-button',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
    this.scrollToTravelExtras.emit();
  }

  private callbackOrOpenEmailModalIfPassengerDoesNotHaveEmail(callbackFn: () => void, order: FinnairOrder): void {
    if (!order.passengers[0].email) {
      this.emailModalOpen.emit(true);
    } else {
      callbackFn();
    }
  }
}
